import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Wand2, Copy, ExternalLink, MessageSquare, Mail,
  Linkedin as LinkedinIcon, MessagesSquare, CalendarClock, Plus, Trash2,
  Save, UserPlus, Search, ChevronRight, ChevronDown, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PhoneInput from "@/components/PhoneInput";

const STATUSES = ["new", "contacted", "qualified", "meeting", "won", "lost", "disqualified", "deferred"];
const EMAIL_KINDS = [
  { key: "cold", label: "Cold Email", icon: Mail },
  { key: "followup", label: "Follow-up", icon: Mail },
  { key: "linkedin", label: "LinkedIn", icon: LinkedinIcon },
  { key: "whatsapp", label: "WhatsApp", icon: MessagesSquare },
  { key: "meeting", label: "Meeting Request", icon: CalendarClock },
];

export default function GrowthLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [genOut, setGenOut] = useState("");
  const [genKind, setGenKind] = useState("cold");
  const [sender, setSender] = useState({ name: "", business: "", value_prop: "" });

  // Unified draft state. All edits happen here until UPDATE is clicked; only
  // then does the diff get PATCHed. Prevents mid-edit saves from creating
  // noisy activity_log entries.
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [dmSearch, setDmSearch] = useState({ open: false, busy: false, results: null });
  const [showActivity, setShowActivity] = useState(true);

  const resetDraftFromLead = (l) => {
    setDraft({
      email: l.email || "",
      phone: l.phone || "",
      website: l.website || "",
      instagram: l.instagram || "",
      facebook: l.facebook || "",
      linkedin: l.linkedin || "",
      status: l.status || "new",
      followup_date: l.followup_date || "",
      decision_makers: (l.decision_makers || []).map((d) => ({ ...d })),
      follow_ups: (l.follow_ups || []).map((f) => ({ ...f })),
      notes: l.notes || "",
      manual_intelligence: l.manual_intelligence || "",
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/growth/leads/${id}`);
      setLead(data);
      resetDraftFromLead(data);
      // Prefill sender from tenant/user if empty
      if (!sender.business) {
        try {
          const me = await api.get("/tenant/me");
          setSender((s) => ({ ...s, business: me.data?.name || "", name: s.name || "" }));
          const u = await api.get("/auth/me");
          setSender((s) => ({ ...s, name: u.data?.name || s.name }));
        } catch {}
      }
    } catch {
      toast.error("Lead not found");
      nav("/app/growth/leads");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Compute pending changes vs saved lead. Only fields that actually differ
  // are sent so the backend's activity_log stays clean.
  const diff = useMemo(() => {
    if (!lead) return {};
    const out = {};
    const scalarKeys = ["email", "phone", "website", "instagram", "facebook", "linkedin", "status", "followup_date", "notes", "manual_intelligence"];
    for (const k of scalarKeys) {
      if ((draft[k] || "") !== (lead[k] || "")) out[k] = draft[k] || null;
    }
    if (JSON.stringify(draft.decision_makers || []) !== JSON.stringify(lead.decision_makers || [])) {
      out.decision_makers = draft.decision_makers || [];
    }
    if (JSON.stringify(draft.follow_ups || []) !== JSON.stringify(lead.follow_ups || [])) {
      out.follow_ups = draft.follow_ups || [];
    }
    return out;
  }, [lead, draft]);
  const dirty = Object.keys(diff).length > 0;

  const saveAll = async () => {
    if (!dirty) { toast.info("No changes to save."); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/growth/leads/${id}`, diff);
      setLead(data);
      resetDraftFromLead(data);
      toast.success("Lead updated — activity log entry recorded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const discardChanges = () => {
    if (lead) resetDraftFromLead(lead);
    toast.info("Discarded unsaved changes.");
  };

  const updateField = async (patch) => {
    // Used only by AI enrichment path — bypasses the draft flow.
    try {
      const { data } = await api.patch(`/growth/leads/${id}`, patch);
      setLead(data);
      resetDraftFromLead(data);
    } catch { toast.error("Update failed"); }
  };

  const enrich = async () => {
    // No hard website requirement — backend can work from social URLs or manual context too.
    // Auto-save any pending edits so enrichment sees the latest sources.
    if (dirty) {
      try {
        await api.patch(`/growth/leads/${id}`, diff);
      } catch { /* enrichment will still try with the previously saved data */ }
    }
    setEnriching(true);
    try {
      const { data } = await api.post(`/growth/leads/${id}/enrich`);
      setLead(data);
      resetDraftFromLead(data);
      toast.success(`Enriched · score ${data.lead_score} (${data.temperature})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed.");
    } finally { setEnriching(false); }
  };

  const generateEmail = async () => {
    if (!sender.name || !sender.business) { toast.error("Fill your name and business first."); return; }
    setEmailBusy(true); setGenOut("");
    try {
      const { data } = await api.post(`/growth/leads/${id}/email`, {
        email_type: genKind,
        sender_name: sender.name,
        sender_business: sender.business,
        value_prop: sender.value_prop || null,
      });
      setGenOut(data.content);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed.");
    } finally { setEmailBusy(false); }
  };

  if (loading || !lead) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const s = lead.ai_summary || {};

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="growth-lead-detail">
      <Link to="/app/growth/leads" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground" data-testid="lead-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to leads
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column — lead facts */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">{lead.industry || "Uncategorised"}</div>
            <div className="flex items-baseline gap-3 mt-1">
              <h1 className="font-display text-4xl">{lead.business_name}</h1>
              {lead.website && <a href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /> Visit</a>}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {lead.city && <span>{lead.city}{lead.country ? `, ${lead.country}` : ""}</span>}
              {typeof lead.lead_score === "number" && <span>Score: <span className="text-foreground font-medium tabular-nums">{lead.lead_score}</span></span>}
              {lead.temperature && <span className="capitalize">Temperature: <span className="text-foreground">{lead.temperature}</span></span>}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">AI Intelligence · Analyse anything</div>
              <Button
                disabled={enriching}
                onClick={enrich}
                className="rounded-full gap-2"
                size="sm"
                data-testid="lead-enrich"
              >
                <Wand2 className="h-4 w-4" />
                {enriching ? "Analysing…" : lead.ai_enriched_at ? "Re-analyse" : "Analyse & Score"}
              </Button>
            </div>

            <p className="mt-2 text-xs text-muted-foreground">
              Kairosa combines <strong>website</strong>, <strong>social URLs</strong> (best-effort) and your <strong>manually pasted context</strong> — whichever exists — into a single AI qualification score.
            </p>

            {/* Sources — website + IG + FB (all drive the unified `draft`) */}
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground">Website URL</label>
                <input
                  data-testid="lead-website-inline"
                  value={draft.website || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))}
                  placeholder="https://example.com"
                  className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Instagram URL</label>
                <input
                  data-testid="lead-instagram-inline"
                  value={draft.instagram || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, instagram: e.target.value }))}
                  placeholder="https://instagram.com/handle"
                  className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Facebook URL</label>
                <input
                  data-testid="lead-facebook-inline"
                  value={draft.facebook || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, facebook: e.target.value }))}
                  placeholder="https://facebook.com/page"
                  className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                />
              </div>
            </div>

            {/* Manual Intelligence textarea */}
            <div className="mt-4">
              <label className="text-xs text-muted-foreground">Manual Intelligence <span className="text-muted-foreground/70">— always works, even when no website exists</span></label>
              <textarea
                data-testid="lead-manual-intel"
                value={draft.manual_intelligence || ""}
                onChange={(e) => setDraft((d) => ({ ...d, manual_intelligence: e.target.value }))}
                rows={4}
                placeholder="Paste anything you know — Instagram bio, recent post captions, what the shop looks like, their vibe, product photos you saw, what you overheard, etc. The more you paste, the better the score."
                className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
              <div className="mt-1.5 text-[10px] text-muted-foreground">
                {(draft.manual_intelligence || "").length} / 5000 characters · Click UPDATE (right column) to save
              </div>
            </div>

            {/* Source badges after analysis */}
            {Array.isArray(lead.ai_sources) && lead.ai_sources.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {lead.ai_sources.map((s, i) => (
                  <span key={i} className={"text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full " + (s.blocked ? "bg-muted text-muted-foreground line-through" : s.used ? "bg-primary/15 text-primary" : "bg-secondary")}>
                    {s.kind} {s.blocked && "· blocked"} {!s.blocked && s.used && "✓"}
                  </span>
                ))}
              </div>
            )}

            {!lead.ai_enriched_at ? (
              <div className="mt-5 text-sm text-muted-foreground border-t border-border pt-4">
                Add at least one source above, then click <strong>Analyse & Score</strong>. If a website/social link is blocked by bot protection, paste details into Manual Intelligence and analyse again.
              </div>
            ) : (
              <div className="mt-4 space-y-3 text-sm">
                {s.summary && <p className="leading-relaxed">{s.summary}</p>}
                <div className="grid sm:grid-cols-2 gap-3">
                  {s.mission && <div><span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Mission</span><div>{s.mission}</div></div>}
                  {s.brand_tone && <div><span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Brand tone</span><div className="capitalize">{s.brand_tone}</div></div>}
                  {Array.isArray(s.services) && s.services.length > 0 && (
                    <div className="sm:col-span-2"><span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Services</span>
                      <div className="mt-1 flex flex-wrap gap-1.5">{s.services.slice(0, 12).map((x, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-secondary">{x}</span>)}</div>
                    </div>
                  )}
                  {Array.isArray(s.buying_signals) && s.buying_signals.length > 0 && (
                    <div className="sm:col-span-2"><span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Buying signals</span>
                      <ul className="mt-1 list-disc list-inside">{s.buying_signals.slice(0, 6).map((x, i) => <li key={i} className="text-sm">{x}</li>)}</ul>
                    </div>
                  )}
                  {typeof s.premium_positioning === "boolean" && <div><span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Premium positioning</span><div>{s.premium_positioning ? "Yes" : "No"}</div></div>}
                  {s.coffee_focus && <div><span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Coffee focus</span><div className="capitalize">{s.coffee_focus}</div></div>}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">AI Outreach Generator</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Your name</label>
                <input data-testid="gen-sender-name" value={sender.name} onChange={(e) => setSender((s) => ({ ...s, name: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Your business</label>
                <input data-testid="gen-sender-business" value={sender.business} onChange={(e) => setSender((s) => ({ ...s, business: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">What you offer</label>
                <input data-testid="gen-sender-value" value={sender.value_prop} onChange={(e) => setSender((s) => ({ ...s, value_prop: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-border bg-background px-3 text-sm" placeholder="Ethiopian single-origin, direct trade…" />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {EMAIL_KINDS.map(({ key, label, icon: I }) => (
                <button key={key}
                  onClick={() => setGenKind(key)}
                  data-testid={`gen-kind-${key}`}
                  className={"inline-flex items-center gap-1.5 text-xs rounded-full border px-3 py-1.5 transition " + (genKind === key ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-foreground/40")}>
                  <I className="h-3.5 w-3.5" /> {label}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={generateEmail} disabled={emailBusy} className="rounded-full gap-2" data-testid="gen-run">
                <Sparkles className="h-4 w-4" />
                {emailBusy ? "Writing…" : "Generate"}
              </Button>
            </div>
            {genOut && (
              <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-4 relative">
                <button onClick={() => { navigator.clipboard.writeText(genOut); toast.success("Copied"); }} className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded hover:bg-foreground/10" aria-label="Copy" data-testid="gen-copy">
                  <Copy className="h-4 w-4" />
                </button>
                <pre className="whitespace-pre-wrap text-sm font-sans">{genOut}</pre>
              </div>
            )}
          </div>
        </div>

        {/* Right — draft-based editor with single UPDATE button */}
        <div className="space-y-4">
          {/* Sticky UPDATE ribbon */}
          <div
            className={
              "sticky top-4 z-10 rounded-xl border p-3 flex items-center gap-2 shadow-sm " +
              (dirty
                ? "border-primary/50 bg-primary/10"
                : "border-border bg-card")
            }
            data-testid="lead-update-ribbon"
          >
            <div className="flex-1 text-xs">
              {dirty ? (
                <>
                  <b className="text-primary">{Object.keys(diff).length} unsaved change{Object.keys(diff).length === 1 ? "" : "s"}</b>
                  <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                    {Object.keys(diff).map((k) => k.replace(/_/g, " ")).join(" · ")}
                  </div>
                </>
              ) : (
                <span className="text-muted-foreground">All changes saved</span>
              )}
            </div>
            {dirty && (
              <button
                onClick={discardChanges}
                data-testid="lead-discard"
                className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground px-2 py-1"
              >
                Discard
              </button>
            )}
            <Button
              size="sm"
              className="rounded-full gap-1.5"
              onClick={saveAll}
              disabled={!dirty || saving}
              data-testid="lead-update"
            >
              <Save className="h-3.5 w-3.5" />
              {saving ? "Saving…" : "UPDATE"}
            </Button>
          </div>

          {/* Pipeline — status + numbered Follow-ups */}
          <div className="rounded-xl border border-border bg-card p-5" data-testid="lead-pipeline">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Pipeline</div>
            <label className="text-xs">Status</label>
            <select
              value={draft.status || "new"}
              onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
              className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 text-sm capitalize"
              data-testid="detail-status"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <div className="mt-4 flex items-baseline justify-between">
              <label className="text-xs">Follow-ups</label>
              <button
                type="button"
                onClick={() => {
                  const next = (draft.follow_ups || []).slice();
                  next.push({ sequence: next.length + 1, date: new Date().toISOString().slice(0, 10), note: "" });
                  setDraft((d) => ({ ...d, follow_ups: next }));
                }}
                data-testid="lead-followup-add"
                className="text-[10px] uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1 hover:opacity-80"
              >
                <Plus className="h-3 w-3" /> Add follow-up
              </button>
            </div>
            {(draft.follow_ups || []).length === 0 ? (
              <div className="mt-2 text-[11px] text-muted-foreground">No follow-ups logged yet.</div>
            ) : (
              <div className="mt-2 space-y-2">
                {(draft.follow_ups || []).map((f, i) => (
                  <div key={i} className="rounded-md border border-border bg-background/50 p-2.5" data-testid={`lead-followup-row-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-medium">
                        Follow-up {f.sequence || i + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const next = (draft.follow_ups || []).filter((_, j) => j !== i)
                            .map((x, idx) => ({ ...x, sequence: idx + 1 }));
                          setDraft((d) => ({ ...d, follow_ups: next }));
                        }}
                        data-testid={`lead-followup-remove-${i}`}
                        className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={f.date || ""}
                        onChange={(e) => {
                          const next = (draft.follow_ups || []).slice();
                          next[i] = { ...next[i], date: e.target.value };
                          setDraft((d) => ({ ...d, follow_ups: next }));
                        }}
                        className="h-8 rounded border border-border bg-background px-2 text-xs"
                        data-testid={`lead-followup-date-${i}`}
                      />
                      <input
                        type="text"
                        value={f.outcome || ""}
                        onChange={(e) => {
                          const next = (draft.follow_ups || []).slice();
                          next[i] = { ...next[i], outcome: e.target.value };
                          setDraft((d) => ({ ...d, follow_ups: next }));
                        }}
                        placeholder="Outcome (optional)"
                        className="h-8 rounded border border-border bg-background px-2 text-xs"
                      />
                    </div>
                    <input
                      type="text"
                      value={f.note || ""}
                      onChange={(e) => {
                        const next = (draft.follow_ups || []).slice();
                        next[i] = { ...next[i], note: e.target.value };
                        setDraft((d) => ({ ...d, follow_ups: next }));
                      }}
                      placeholder="Note — what was discussed / channel / etc"
                      className="mt-1.5 h-8 w-full rounded border border-border bg-background px-2 text-xs"
                      data-testid={`lead-followup-note-${i}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact — editable */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="lead-contact-block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Contact</div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Email</label>
              <input
                data-testid="lead-contact-email"
                value={draft.email || ""}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                placeholder="purchase@example.com"
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2.5 text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Phone</label>
              <div className="mt-1">
                <PhoneInput
                  value={draft.phone || ""}
                  defaultCountry={draft.country}
                  onChange={(v) => setDraft((d) => ({ ...d, phone: v }))}
                  testId="lead-contact-phone"
                />
              </div>
            </div>
            {[
              { k: "website", label: "Website", placeholder: "https://…" },
              { k: "instagram", label: "Instagram", placeholder: "https://instagram.com/…" },
              { k: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/…" },
              { k: "facebook", label: "Facebook", placeholder: "https://facebook.com/…" },
            ].map(({ k, label, placeholder }) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</label>
                <input
                  data-testid={`lead-contact-${k}`}
                  value={draft[k] || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                  placeholder={placeholder}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2.5 text-xs"
                />
              </div>
            ))}
          </div>

          {/* Decision Makers — multi-entry + Find on LinkedIn */}
          <div className="rounded-xl border border-border bg-card p-5" data-testid="lead-dm-block">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Decision Makers</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setDmSearch({ open: true, busy: true, results: null });
                    try {
                      const { data } = await api.post(`/growth/leads/${id}/find-decision-makers`);
                      setDmSearch({ open: true, busy: false, results: data });
                    } catch (e) {
                      setDmSearch({ open: true, busy: false, results: { ok: false, message: e?.response?.data?.detail || "Search failed" } });
                    }
                  }}
                  data-testid="lead-dm-find"
                  className="text-[10px] uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1 hover:opacity-80"
                >
                  <Search className="h-3 w-3" /> Find on LinkedIn
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = (draft.decision_makers || []).slice();
                    next.push({ name: "", role: "", email: "", phone: "", linkedin: "", department: "" });
                    setDraft((d) => ({ ...d, decision_makers: next }));
                  }}
                  data-testid="lead-dm-add"
                  className="text-[10px] uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1 hover:opacity-80"
                >
                  <UserPlus className="h-3 w-3" /> Add
                </button>
              </div>
            </div>

            {(draft.decision_makers || []).length === 0 ? (
              <div className="text-[11px] text-muted-foreground">Not identified yet. Click <b>Add</b> or <b>Find on LinkedIn</b>.</div>
            ) : (
              <div className="space-y-3">
                {(draft.decision_makers || []).map((dm, i) => (
                  <div key={i} className="rounded-md border border-border bg-background/50 p-3 space-y-2" data-testid={`lead-dm-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Contact {i + 1}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const next = (draft.decision_makers || []).filter((_, j) => j !== i);
                          setDraft((d) => ({ ...d, decision_makers: next }));
                        }}
                        data-testid={`lead-dm-remove-${i}`}
                        className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        ["name", "Full name"],
                        ["role", "Role / title"],
                        ["email", "Email"],
                        ["phone", "Phone"],
                        ["linkedin", "LinkedIn URL"],
                        ["department", "Department"],
                      ].map(([k, ph]) => (
                        <input
                          key={k}
                          value={dm[k] || ""}
                          onChange={(e) => {
                            const next = (draft.decision_makers || []).slice();
                            next[i] = { ...next[i], [k]: e.target.value };
                            setDraft((d) => ({ ...d, decision_makers: next }));
                          }}
                          placeholder={ph}
                          data-testid={`lead-dm-${i}-${k}`}
                          className={"h-8 rounded border border-border bg-background px-2 text-xs " + (k === "linkedin" ? "col-span-2" : "")}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {dmSearch.open && (
              <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="lead-dm-search-panel">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-primary">
                    LinkedIn candidates (via Google)
                  </span>
                  <button
                    onClick={() => setDmSearch({ open: false, busy: false, results: null })}
                    className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
                  >Close</button>
                </div>
                {dmSearch.busy && <div className="text-muted-foreground">Searching…</div>}
                {!dmSearch.busy && dmSearch.results && (
                  <>
                    {dmSearch.results.message && (
                      <div className="text-muted-foreground mb-2 leading-relaxed">{dmSearch.results.message}</div>
                    )}
                    {dmSearch.results.signup_url && (
                      <a href={dmSearch.results.signup_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                        Sign up for Serper.dev →
                      </a>
                    )}
                    {(dmSearch.results.candidates || []).map((cand, i) => (
                      <div key={i} className="mt-2 border border-border bg-card rounded p-2 flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{cand.name || cand.linkedin}</div>
                          {cand.role && <div className="text-[11px] text-muted-foreground">{cand.role}</div>}
                          <a href={cand.linkedin} target="_blank" rel="noopener noreferrer" className="text-[11px] text-primary underline truncate block">{cand.linkedin}</a>
                          {cand.snippet && <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{cand.snippet}</div>}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-[10px] gap-1 h-7"
                          onClick={() => {
                            const next = (draft.decision_makers || []).slice();
                            next.push({ name: cand.name, role: cand.role, linkedin: cand.linkedin });
                            setDraft((d) => ({ ...d, decision_makers: next }));
                            toast.success("Added — click UPDATE to save.");
                          }}
                          data-testid={`lead-dm-candidate-add-${i}`}
                        >
                          <UserPlus className="h-3 w-3" /> Add
                        </Button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Notes — freeform + activity log timeline */}
          <div className="rounded-xl border border-border bg-card p-5" data-testid="lead-notes-block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Notes</div>
            <textarea
              value={draft.notes || ""}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              rows={3}
              placeholder="Freeform notes — anything you want to remember about this lead."
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="lead-notes-input"
            />
          </div>

          {/* Activity Log — collapsible timeline of every change */}
          <div className="rounded-xl border border-border bg-card p-5" data-testid="lead-activity-block">
            <button
              type="button"
              onClick={() => setShowActivity((v) => !v)}
              className="w-full flex items-center justify-between"
              data-testid="lead-activity-toggle"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground inline-flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Activity log
                {(lead.activity_log || []).length > 0 && (
                  <span className="ml-1 text-primary">· {(lead.activity_log || []).length}</span>
                )}
              </span>
              {showActivity ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            </button>
            {showActivity && (
              <div className="mt-3 space-y-3">
                {(lead.activity_log || []).length === 0 ? (
                  <div className="text-[11px] text-muted-foreground">
                    No activity yet. Every change you save here will appear in this timeline.
                  </div>
                ) : (
                  [...(lead.activity_log || [])].reverse().map((entry, i) => (
                    <div key={i} className="border-l-2 border-primary/40 pl-3" data-testid={`lead-activity-entry-${i}`}>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-2">
                        <span>{new Date(entry.at).toLocaleString()}</span>
                        <span className="opacity-40">·</span>
                        <span className="text-foreground/80">{entry.actor}</span>
                      </div>
                      {entry.summary && (
                        <div className="mt-1 text-xs text-primary font-medium">{entry.summary}</div>
                      )}
                      {(entry.changes || []).map((c, j) => (
                        <div key={j} className="mt-1 text-xs">
                          <span className="text-muted-foreground">{c.label}:</span>{" "}
                          <span className="text-muted-foreground line-through opacity-70">{c.from}</span>{" "}
                          <span className="opacity-60">→</span>{" "}
                          <span className="text-foreground">{c.to}</span>
                        </div>
                      ))}
                    </div>
                  ))
                )}
                {/* Also surface the original creation note as the origin point */}
                {lead.notes && (
                  <div className="border-l-2 border-muted-foreground/30 pl-3 mt-2 pt-2 border-t border-dashed border-border">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Origin note · {lead.created_at ? new Date(lead.created_at).toLocaleString() : ""}
                    </div>
                    <div className="text-xs whitespace-pre-wrap mt-1 text-muted-foreground italic">
                      {lead.notes}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
